<script setup lang="ts">
import { useContactModals } from '~/composables/useContactModals';
import cdnAssetPath from '~/functions/cdnAssetPath';

const {
  showRegisterModal,
  registerFormLoading,
  onRegisterFormLoad,
  showContributorContactModal,
  contributorFormData,
  firstName,
  lastName,
  email,
  comments,
  submitting,
  submitted,
  submitError,
  fieldErrors,
  submitContributorContactForm,
  closeContributorContactModal
} = useContactModals();
</script>

<template>
  <!-- Register Interest Modal -->
  <Dialog
    v-model:visible="showRegisterModal"
    modal
    header="Register Your Interest"
    :style="{ width: '90vw', maxWidth: '600px', height: '84vh' }"
    :draggable="false"
    :closable="true"
    :pt="{
      content: { style: 'height: 100%; padding: 0;' }
    }">
    <div class="relative flex h-full flex-col p-4">
      <!-- Loading Spinner -->
      <div v-if="registerFormLoading" class="flex flex-1 items-center justify-center">
        <ProgressSpinner />
      </div>
      <!-- Brevo iframe -->
      <iframe
        v-show="!registerFormLoading"
        src="https://8e554e48.sibforms.com/serve/MUIFAHHfQQYCHF7ZGczylR5IGCYTHKT5mOplMbF_3LLzSk1HA6Pu15QA0Wxza0dM2paINHWhbmI8sFRbtXjdecBFLxD8CCn2uKmRPG0T-3QFA6ZAz7aa3lyAgsVMMdJCkTthSf9NHlmjc4n0O2eLixjr8Usoljm7miGSOlGaFVQBb887q7F4SfkLb0mNM4sJ7M7XyvX6bUgDLO-C"
        class="w-full flex-1 border-0"
        frameborder="0"
        scrolling="auto"
        allowfullscreen
        title="Register Interest Form"
        @load="onRegisterFormLoad">
        Loading…
      </iframe>
    </div>
  </Dialog>

  <!-- Contributor Contact Modal -->
  <Dialog
    v-model:visible="showContributorContactModal"
    modal
    header=""
    :style="{ width: '90vw', maxWidth: '600px' }"
    :draggable="false"
    :closable="true"
    :pt="{
      content: { style: 'padding: 0;' }
    }">
    <div class="p-4">
      <!-- Success state -->
      <div v-if="submitted" class="py-8 text-center">
        <i class="pi pi-check-circle mb-4 text-5xl text-green-600"></i>
        <p class="mt-4 text-lg font-semibold">Your request has been submitted.</p>
        <p class="mt-2 text-gray-600">We'll be in touch soon.</p>
        <button
          class="mt-6 rounded-md bg-brand-title px-6 py-2 text-white hover:opacity-90"
          @click="closeContributorContactModal()">
          Close
        </button>
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="submitContributorContactForm">
        <!-- Contributor photo -->
        <div v-if="contributorFormData.imageUrl" class="mb-4 flex justify-center">
          <div class="relative size-28 overflow-hidden rounded-full">
            <img
              :src="cdnAssetPath(contributorFormData.imageUrl)"
              :alt="contributorFormData.contributor"
              class="absolute inset-0 size-full object-cover" />
          </div>
        </div>

        <h3 class="mb-6 text-center text-xl font-bold">
          Request a coaching session with {{ contributorFormData.contributor }}
        </h3>

        <!-- General error -->
        <div v-if="submitError" class="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {{ submitError }}
        </div>

        <!-- First name -->
        <div class="mb-4">
          <label for="contributor-firstname" class="mb-1 block text-sm font-semibold text-gray-700">First Name *</label>
          <input
            id="contributor-firstname"
            v-model="firstName"
            type="text"
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-secondary focus:outline-none focus:ring-1 focus:ring-brand-secondary"
            :class="{ 'border-red-400': fieldErrors.first_name }" />
          <p v-if="fieldErrors.first_name" class="mt-1 text-xs text-red-600">{{ fieldErrors.first_name[0] }}</p>
        </div>

        <!-- Last name -->
        <div class="mb-4">
          <label for="contributor-lastname" class="mb-1 block text-sm font-semibold text-gray-700">Last Name *</label>
          <input
            id="contributor-lastname"
            v-model="lastName"
            type="text"
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-secondary focus:outline-none focus:ring-1 focus:ring-brand-secondary"
            :class="{ 'border-red-400': fieldErrors.last_name }" />
          <p v-if="fieldErrors.last_name" class="mt-1 text-xs text-red-600">{{ fieldErrors.last_name[0] }}</p>
        </div>

        <!-- Email -->
        <div class="mb-4">
          <label for="contributor-email" class="mb-1 block text-sm font-semibold text-gray-700">Email *</label>
          <input
            id="contributor-email"
            v-model="email"
            type="text"
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-secondary focus:outline-none focus:ring-1 focus:ring-brand-secondary"
            :class="{ 'border-red-400': fieldErrors.email }" />
          <p v-if="fieldErrors.email" class="mt-1 text-xs text-red-600">{{ fieldErrors.email[0] }}</p>
        </div>

        <!-- Comments -->
        <div class="mb-4">
          <label for="contributor-comments" class="mb-1 block text-sm font-semibold text-gray-700">Comments *</label>
          <textarea
            id="contributor-comments"
            v-model="comments"
            rows="4"
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-secondary focus:outline-none focus:ring-1 focus:ring-brand-secondary"
            :class="{ 'border-red-400': fieldErrors.comments }"></textarea>
          <p v-if="fieldErrors.comments" class="mt-1 text-xs text-red-600">{{ fieldErrors.comments[0] }}</p>
        </div>

        <!-- Honeypot -->
        <input type="text" name="email_address_check" value="" class="hidden" tabindex="-1" autocomplete="off" />

        <!-- Submit -->
        <div class="text-center">
          <button
            type="submit"
            :disabled="submitting"
            class="inline-flex items-center gap-2 rounded-md bg-brand-title px-6 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-60">
            <ProgressSpinner v-if="submitting" style="width: 18px; height: 18px;" strokeWidth="4" />
            <span>{{ submitting ? 'Submitting...' : 'Request Info' }}</span>
          </button>
        </div>
      </form>
    </div>
  </Dialog>
</template>

<style scoped></style>
